import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Package, FileSpreadsheet, TrendingUp, TrendingDown, Plus, ArrowRight,
  DollarSign, Percent, ClipboardList
} from "lucide-react";
import { formatCurrency, formatPercent } from "@/lib/utils";
import {
  PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip
} from "recharts";
import { API } from "@/lib/api";

const COLORS = ["#10b981", "#ef4444"];

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentSheets, setRecentSheets] = useState([]);
  const [technicalSheetsCount, setTechnicalSheetsCount] = useState(0);
  const [marginData, setMarginData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, sheetsRes, techRes, marginsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/cost-sheets`),
        axios.get(`${API}/technical-sheets`),
        axios.get(`${API}/reports/margins`)
      ]);
      setStats(statsRes.data);
      setRecentSheets(sheetsRes.data.slice(0, 5));
      setTechnicalSheetsCount(techRes.data.length);
      setMarginData(marginsRes.data.items || []);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;
  }

  const pieData = [
    { name: "Com Lucro", value: stats?.profitable_count || 0 },
    { name: "Com Prejuizo", value: stats?.unprofitable_count || 0 },
  ].filter(d => d.value > 0);

  const barData = marginData.slice(0, 8).map(d => ({
    name: d.name.length > 12 ? d.name.slice(0, 12) + "..." : d.name,
    fullName: d.name,
    margin: d.contribution_margin_percent,
    profitable: d.is_profitable,
  }));

  return (
    <div className="space-y-8 animate-slide-in" data-testid="dashboard-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Manrope' }}>
            Dashboard
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">Visao geral da precificacao</p>
        </div>
        <Link to="/cost-sheets/new">
          <Button className="bg-teal-600 hover:bg-teal-700" data-testid="new-cost-sheet-btn">
            <Plus className="w-4 h-4 mr-2" /> Nova Precificacao
          </Button>
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        <Card className="stat-card cursor-pointer hover:border-blue-300 transition-colors" onClick={() => navigate('/products')} data-testid="stat-products">
          <CardContent className="p-4 h-full">
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-center w-10 h-10 bg-blue-50 dark:bg-blue-900/30 rounded-lg mb-3">
                <Package className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400">Produtos/Insumos</p>
              <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1 font-mono">{stats?.products_count || 0}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card cursor-pointer hover:border-orange-300 transition-colors" onClick={() => navigate('/technical-sheets')} data-testid="stat-technical-sheets">
          <CardContent className="p-4 h-full">
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-center w-10 h-10 bg-orange-50 dark:bg-orange-900/30 rounded-lg mb-3">
                <ClipboardList className="w-5 h-5 text-orange-600 dark:text-orange-400" />
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400">Fichas Tecnicas</p>
              <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1 font-mono">{technicalSheetsCount}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card cursor-pointer hover:border-purple-300 transition-colors" onClick={() => navigate('/cost-sheets')} data-testid="stat-sheets">
          <CardContent className="p-4 h-full">
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-center w-10 h-10 bg-purple-50 dark:bg-purple-900/30 rounded-lg mb-3">
                <FileSpreadsheet className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400">Precificacoes</p>
              <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1 font-mono">{stats?.sheets_count || 0}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card cursor-pointer hover:border-emerald-300 transition-colors" onClick={() => navigate('/reports')} data-testid="stat-profitable">
          <CardContent className="p-4 h-full">
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-center w-10 h-10 bg-emerald-50 dark:bg-emerald-900/30 rounded-lg mb-3">
                <TrendingUp className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400">Com Lucro</p>
              <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 mt-1 font-mono">{stats?.profitable_count || 0}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card cursor-pointer hover:border-rose-300 transition-colors" onClick={() => navigate('/reports')} data-testid="stat-unprofitable">
          <CardContent className="p-4 h-full">
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-center w-10 h-10 bg-rose-50 dark:bg-rose-900/30 rounded-lg mb-3">
                <TrendingDown className="w-5 h-5 text-rose-600 dark:text-rose-400" />
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400">Com Prejuizo</p>
              <p className="text-2xl font-bold text-rose-600 dark:text-rose-400 mt-1 font-mono">{stats?.unprofitable_count || 0}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Summary + Chart */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Card className="stat-card" data-testid="stat-revenue">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-teal-50 dark:bg-teal-900/30 rounded-xl flex items-center justify-center">
                <DollarSign className="w-7 h-7 text-teal-600 dark:text-teal-400" />
              </div>
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Receita Potencial</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1 font-mono">{formatCurrency(stats?.total_potential_revenue || 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card" data-testid="stat-margin-avg">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${(stats?.avg_margin_percent || 0) >= 0 ? 'bg-emerald-50 dark:bg-emerald-900/30' : 'bg-rose-50 dark:bg-rose-900/30'}`}>
                <Percent className={`w-7 h-7 ${(stats?.avg_margin_percent || 0) >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`} />
              </div>
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Margem Media</p>
                <p className={`text-2xl font-bold mt-1 font-mono ${(stats?.avg_margin_percent || 0) >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                  {formatPercent(stats?.avg_margin_percent || 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pie Chart */}
        {pieData.length > 0 && (
          <Card className="stat-card" data-testid="pie-chart">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-2 text-center">Distribuicao</p>
              <div className="h-32">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={30} outerRadius={55} dataKey="value" label={({ name, value }) => `${name}: ${value}`} labelLine={false}>
                      {pieData.map((_, idx) => <Cell key={idx} fill={COLORS[idx % COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Bar Chart - Margins */}
      {barData.length > 0 && (
        <Card data-testid="margin-bar-chart">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>Margens por Procedimento</CardTitle>
              <Link to="/comparison">
                <Button variant="ghost" size="sm" className="text-teal-600">
                  Ver comparativo <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={barData} margin={{ top: 5, right: 20, left: 10, bottom: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                  <XAxis dataKey="name" angle={-30} textAnchor="end" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 12 }} tickFormatter={v => `${v}%`} />
                  <Tooltip content={({ payload }) => {
                    if (!payload?.length) return null;
                    const d = payload[0].payload;
                    return (
                      <div className="bg-white dark:bg-slate-800 p-2 rounded shadow-lg border text-sm">
                        <p className="font-medium">{d.fullName}</p>
                        <p className={d.profitable ? "text-emerald-600" : "text-rose-600"}>Margem: {d.margin}%</p>
                      </div>
                    );
                  }} />
                  <Bar dataKey="margin" radius={[4, 4, 0, 0]}>
                    {barData.map((entry, idx) => (
                      <Cell key={idx} fill={entry.profitable ? "#10b981" : "#ef4444"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Sheets */}
      <Card data-testid="recent-sheets-card">
        <CardHeader className="border-b border-slate-100 dark:border-slate-800 pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Manrope' }}>
              Fichas de Custo Recentes
            </CardTitle>
            <Link to="/cost-sheets">
              <Button variant="ghost" size="sm" className="text-teal-600 hover:text-teal-700">
                Ver todas <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {recentSheets.length === 0 ? (
            <div className="empty-state py-12">
              <FileSpreadsheet className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 dark:text-slate-400">Nenhuma ficha de custo cadastrada</p>
              <Link to="/cost-sheets/new">
                <Button className="mt-4 bg-teal-600 hover:bg-teal-700">
                  <Plus className="w-4 h-4 mr-2" /> Criar primeira ficha
                </Button>
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              {recentSheets.map((sheet) => (
                <Link key={sheet.id} to={`/cost-sheets/${sheet.id}`}
                  className="flex items-center justify-between p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                  data-testid={`recent-sheet-${sheet.id}`}>
                  <div>
                    <p className="font-medium text-slate-900 dark:text-white">{sheet.name}</p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">{sheet.service_type === 'servico' ? 'Servico' : 'Produto'}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono font-medium text-slate-900 dark:text-white">{formatCurrency(sheet.sale_price)}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
